// React
import { useEffect, useState } from "react"

// Components
import { PrintTransaction } from "components"

// Form
import { useFormContext, useWatch } from "react-hook-form"

// Third-Party Libraries
import moment from "moment"

// Types
import { FormReturnItemsType } from "../types"

// Utils
import { convertNumber, useCountryById, useHandleAuth, useProvinceById, useRegencyById, useSubdistrictById, useTaxCode } from "utils"
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"
import { useSubdomainSetting } from "pages/Purchase/Transaction/PurchaseOrder/utils"
import { useCustomerDetail, useDropdownProduct } from "../../CustomerOrder/utils/hooks"
import { generatePath } from "pages/Task/utils/functions"
import { calculateSummaryReturnItems } from "../utils/functions/calculateSummaryReturnItems"

export function Print(): JSX.Element {
  // Form
  const { formState: { defaultValues }, watch, control } = useFormContext<FormReturnItemsType>()
  // Hooks
  const company = useList()
  const country = useCountryById()
  const customer = useCustomerDetail(watch("customer_id") ?? "")
  const product = useDropdownProduct({ location_id: defaultValues?.location_id })
  const province = useProvinceById()
  const regency = useRegencyById()
  const setting = useSubdomainSetting()
  const subdistrict = useSubdistrictById()
  const taxCode = useTaxCode()
  const { userTokenData } = useHandleAuth()
  const [address, setAddress] = useState<string>("")
 const returnDetail = useWatch({
    control,
    name: 'return_detail'
  })

  // Vars
  const isLoading: boolean = company.isLoading || customer.isLoading || country.isLoading || province.isLoading || subdistrict.isLoading || regency.isLoading
  const userToken = userTokenData()
  const summary = calculateSummaryReturnItems(returnDetail ?? [])

 
  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) {
        subdistrict.fetchData(res.subdistrict_id)
      }

      if (res.regency_id) {
        regency.fetchData(res.regency_id)
      }

      if (res.province_id) {
        province.fetchData(res.province_id)
      }

      if (res.country_id) {
        country.fetchData(res.country_id)
      }
    })

    // eslint-disable-next-line
  }, [])

  return (
    <PrintTransaction loading={isLoading}>
      <section className="flex flex-col gap-3">
        <section className="grid grid-cols-2 items-stretch gap-3">
          <img
            src={generatePath(setting.dataCompany?.logo)}
            alt="Logo"
            width={200}
            height={50}
          />

          <section>
            <div className="mb-3 text-black text-xl font-bold text-right">Return Items</div>

            <section className="flex justify-end">
              <table className="w-full max-w-[300px]">
                <tbody>
                  <tr>
                    <td width={10} className="whitespace-nowrap text-right font-bold">Reference :{" "}</td>
                    <td className="whitespace-nowrap text-right">{watch("trx_code")}</td>
                  </tr>

                  <tr>
                    <td width={10} className="whitespace-nowrap text-right font-bold">Date :{" "}</td>
                    <td className="whitespace-nowrap text-right">{defaultValues?.transaction_date ? moment(defaultValues?.transaction_date).format("DD/MM/YYYY") : "-"}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          </section>
        </section>

        <section className="grid grid-cols-2 items-stretch gap-3">
          <section>
            <div className="font-bold">Company Info</div>
            <div className="divider divider-neutral" />
            <div className="font-bold">{userToken?.company_name}</div>
            <div>{address},</div>
            <div>{regency.data?.regency_name},</div>
            <div>{province.data?.province_name},</div>
            <div>Telp: {setting.data?.phone ?? "-"}</div>
            <div>Email: {setting.data?.email ?? "-"}</div>
          </section>

          <section>
            <div className="font-bold">Bill For</div>
            <div className="divider divider-neutral" />
            <div className="font-bold">{customer.data?.customer_name}</div>
            <div>Telp: {customer.data?.handphone}</div>
          </section>
        </section>

        <table className="table table-bordered">
          <thead>
            <tr className="bg-black text-white">
              <th>ITEM CODE</th>
              <th>DESCRIPTION</th>
              <th>BACK DELIVERED</th>
              <th>QTY RETURNED</th>
              <th>PRICE</th>
              <th>DISCOUNT</th>
              <th>TAX</th>
              <th>TOTAL</th>
            </tr>
          </thead>

          <tbody>
            {returnDetail?.map((item, key) => {
              // Vars
              const productData = product.data.find(
                (i) => i.product_id === item?.product_id
              )
              const taxCodeData = taxCode.data.find(
                (i) => i.value === item?.tax_id?.toString()
              )

              return (
                <tr key={key}>
                  <td>{productData?.product_barcode}</td>
                  <td>{productData?.product_name}</td>
                  <td className="text-right">{convertNumber(item.quantity_invoice).intoCurrency}</td>
                  <td className="text-right">{convertNumber(item.quantity_returned).intoCurrency}</td>
                  <td className="text-right">{convertNumber(item.price_invoice).intoCurrency}</td>
                  <td className="text-right">{convertNumber(item.discount).intoCurrency}%</td>
                  <td>{taxCodeData?.label}</td>
                  <td className="text-right">{convertNumber(item.total).intoCurrency}</td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <section className="flex justify-end">
          <table className="table max-w-[400px]">
            <tbody>
              <tr className="border-b">
                <td className="font-bold">Subtotal</td>
                <td className="text-right">{convertNumber(summary.subtotal).intoCurrency}</td>
              </tr>

              <tr className="border-b">
                <td className="font-bold">Tax</td>
                <td className="text-right">{convertNumber(summary.tax).intoCurrency}</td>
              </tr>

              <tr className="border-b">
                <td className="font-bold">Total Reject</td>
                <td className="text-right">{convertNumber(summary.totalReject).intoCurrency}</td>
              </tr>

              <tr className="border-b">
                <td className="font-bold">Balance</td>
                <td className="text-right">{convertNumber(summary.totalReject).intoCurrency}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="grid grid-cols-2 items-stretch gap-3">
          <section>
            <div className="font-bold">Additional Notes</div>
            <div className="divider divider-neutral" />
            <td>{defaultValues?.memo}</td>
          </section>
        </section>
      </section>
    </PrintTransaction>
  )
}